<template>
  <div class="buy-sell-control-market">
    <trade-buy-sell-header
      :trade-type="tradeType"
      :title="formattedTradeType"
      :main-ticker="getCurrentPair.stockTicker"
      :balance="formattedBalance"
      :theme="submitTheme"
      class="buy-sell-control-market__head"
    />

    <form
      autocomplete="off"
      class="pb-20 pt-16 pl-10 pr-10"
      @submit.prevent="handleSubmit"
      @keydown.enter.prevent
    >
      <trade-buy-sell-input
        ref="price"
        :value="$t('trade.buySellControl.market')"
        :placeholder="$t('trade.buySell.price')"
        :currency="getCurrentPair.moneyTicker"
        readonly
        dashed-border
        class="buy-sell-control-market__price mb-16"
        :size="size"
      />
      <trade-buy-sell-input
        ref="amount"
        v-model="amount"
        :value="amount"
        :field="$v.amount"
        :placeholder="$t('trade.buySell.amount')"
        :currency="
          tradeType === TRADE_TYPE.BUY
            ? getCurrentPair.moneyTicker
            : getCurrentPair.stockTicker
        "
        :optional-errors="{
          ...mix_getServerError('amount'),
          ...commonValidationTranslations,
        }"
        :size="size"
        :current-type="tradeType"
        :disabled="!logged"
        autocomplete="off"
        type="number"
        step="0.00000001"
        class="buy-sell-control-market__amount txt txt--green mb-16"
        @input="onInputAmount"
        @blur="$v.amount.$touch()"
      />
      <trade-buy-sell-amount-picker
        :buttons="percentBtns"
        class="buy-sell-control-market__percentage mb-16"
        @click="amountQuickPicker"
        @blur="$v.amount.$touch()"
      />
      <exc-button
        v-if="logged"
        :color="submitTheme"
        :loading="loading"
        size="xs"
        class="buy-sell-control-market__btn"
        type="submit"
        full-width
      >
        {{ tradeType === TRADE_TYPE.BUY ? $t('trade.buy') : $t('trade.sell') }}
      </exc-button>
      <exc-button
        v-else
        color="main"
        outline
        :loading="loading"
        :disabled="$v.$error"
        size="xs"
        class="buy-sell-control-market__btn"
        :to="localePath('/login')"
        full-width
      >
        {{ $t('trade.loginBtn') }}
      </exc-button>
    </form>
  </div>
</template>

<script>
import { ExcButton } from '@/components/common'
import { mapGetters, mapState } from 'vuex'
import * as lockr from 'lockr'
import { ConfirmationModal } from '@/lib/ConfirmationModal'
import { serverFormMixin } from '@/mixins/serverFormMixin'
import { validationMixin } from 'vuelidate'
import { MARGIN_TRADE_CROSS, TRADE_TYPE } from '@/lib/constants'
import { maxValue, required, minValue } from 'vuelidate/lib/validators'
import { valueSetterCutter, noExponents } from '@/lib/utils'

export default {
  name: 'BuySellControlMarket',
  components: {
    ExcButton,
    TradeBuySellHeader: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellHeader.vue'
      ),
    TradeBuySellInput: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellInput.vue'
      ),
    TradeBuySellAmountPicker: () =>
      import(
        /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/TradeBuySellAmountPicker.vue'
      ),
    // MarginTradeModeSelector: () =>
    //   import(
    //     /* webpackMode: "eager" */ '@/components/trade/trade-buy-sell-elements/MarginTradeModeSelector.vue'
    //   ),
  },
  mixins: [validationMixin, serverFormMixin],
  props: {
    tradeType: {
      validator: tradeType => Object.values(TRADE_TYPE).includes(tradeType),
      required: true,
    },
    size: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      currentMarginType: 'borrow',
      percentBtns: [
        { name: '25%', type: 0.25 },
        { name: '50%', type: 0.5 },
        { name: '75%', type: 0.75 },
        { name: '100%', type: 1 },
      ],
      loading: false,
      amount: null,

      TRADE_TYPE,
    }
  },
  computed: {
    ...mapState({
      logged: state => state.auth.loggedIn,
      clearErrorsTimeToWatch: state =>
        state.buySell.form.clearErrorsTimeToWatch,
    }),
    ...mapGetters([
      // "getCurrentPair",
      // "getCurrencyAvailableBalance",
    ]),
    ...mapGetters({
      getCurrentPair: 'pairs/getCurrentPair',
      getMarginBalanceBuyPairAndTicker:
        'wallet/getMarginBalanceBuyPairAndTicker',
      getCurrencyAvailableBalance: 'wallet/getCurrencyAvailableBalance',
      getMarginBalanceByTicker: 'wallet/getMarginBalanceByTicker',
    }),
    formattedTradeType () {
      return this.tradeType === TRADE_TYPE.BUY
        ? this.$t('trade.buy')
        : this.$t('trade.sell')
    },
    formattedBalance () {
      return ` ${
        this.balance.greaterThan(0)
          ? noExponents(this.balance.toDecimalPlaces(8).toNumber())
          : '0'
      } ${this.balanceTicker ? this.balanceTicker : ''}`
    },
    isMarginTrade () {
      return this.$store.state.trade.currentTradeType === MARGIN_TRADE_CROSS
    },
    balance () {
      // console.log(this.$store.state.currentTradeType)
      if (this.isMarginTrade) {
        return this.getMarginBalanceByTicker(this.balanceTicker).marginBalance
        // return valDecimal
      }
      return this.getCurrencyAvailableBalance(this.balanceTicker)
    },
    balanceTicker () {
      return this.tradeType === TRADE_TYPE.BUY
        ? this.getCurrentPair.moneyTicker // second
        : this.getCurrentPair.stockTicker // first
    },
    commonValidationTranslations () {
      return {
        maxValue: this.$t('validators.maxValue', {
          max: this.balance.toNumber(),
        }),
        required: this.$t('validators.required'),
        minValue: this.$t('validators.minValue', {
          min: this.getCurrentPair.minAmountStock,
        }),
        decimalOnly: this.$t('validators.onlyNumbers'),
      }
    },
    submitTheme () {
      return this.tradeType === TRADE_TYPE.BUY ? 'green' : 'red'
    },
    // amount: {
    //     get() {
    //         return this.amountInData
    //     },
    //     set(v) {
    //         this.amountInData = valueSetterCutter(v)
    //         // console.log(this.amountInData)
    //         this.$v.amount.$touch()
    //         // this.$forceUpdate()
    //         // console.log(this.amountInData, v)
    //     }
    // }
  },

  watch: {
    tradeType () {
      this.amount = null
      this.$v.$reset()
    },
    amount () {
      this.mix_clearServerError('amount')
    },
  },

  methods: {
    onInputAmount (v) {
      // this.amount = v
      this.amount = valueSetterCutter(v)
      this.$v.amount.$touch()
    },
    async handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) return

      if (lockr.get('trade-confirmation-state')) {
        const confirmed = await this.showConfirmation()
        if (!confirmed) return
      }

      const body = {
        market: this.getCurrentPair.pairName,
        amount: this.amount,
        direction: this.tradeType === TRADE_TYPE.BUY ? '2' : '1',
      }

      this.loading = true
      const { errors, variables } = this.isMarginTrade
        ? await this.$api.putMarketCrossOrder({ body })
        : await this.$api.PutMarketOrder({ body })

      if (errors && errors.form) errors.amount = errors.form

      this.$v.$reset()
      this.loading = false

      if (errors) {
        if (errors?.message) {
          this.$notify({
            type: 'error',
            title: this.$t(errors.message[0]),
          })
        }

        this.mix_setServerErrors(errors, variables)
        return
      }

      this.$v.$reset()
    },
    resetForm () {
      this.amount = null

      this.$v.$reset()
    },
    amountQuickPicker ({ type }) {
      const fromBalance = this.balance.times(type).toString()

      // to calculate sum with fee
      // if (this.tradeType === TRADE_TYPE.BUY) {
      //   this.amount = this.getAmountWithoutFeed(fromBalance)
      //
      //   return
      // }
      // end sum with fee

      this.amount = valueSetterCutter(fromBalance)
    },
    showConfirmation () {
      const { amount, tradeType, getCurrentPair } = this
      const action =
        tradeType === TRADE_TYPE.BUY
          ? this.$t('trade.buy')
          : this.$t('trade.sell')
      const currency = getCurrentPair.stockTicker
      const text =
        tradeType === TRADE_TYPE.BUY
          ? this.$t('common.confirmationModal.buyMarketText', {
            amount,
            buyTicker: getCurrentPair.stockTicker,
            sellTicker: getCurrentPair.moneyTicker,
          })
          : this.$t('common.confirmationModal.sellMarketText', {
            amount,
            buyTicker: getCurrentPair.stockTicker,
            sellTicker: getCurrentPair.moneyTicker,
          })
      return ConfirmationModal.open({
        title: `${action} ${currency}`,
        text,
        confirmationImg: 'buy.svg',
      })
    },
  },

  validations () {
    return this.mix_validationsServerWrapper({
      amount: {
        required,
        decimalOnly: v => !/[+-]/.test(v),
        minValue: minValue(
          (this.getCurrentPair && this.getCurrentPair.minAmountStock) || 0,
        ),
        maxValue: maxValue(this.balance.toNumber()),
      },
    })
  },
}
</script>

<style lang="scss" scoped>
.buy-sell-control-market {
  &__btn {
  }
}
</style>
